import * as React from "react"
import { ReactSortable } from "react-sortablejs"
import { Data } from "rete/types/core/data"

import { LayerListRow } from "../../shared/layer_list_row"
import { keyToSlot, slotToKey, type MapStackSlot } from "../map/map_layer_stack"
import type { LocalGeotiffLayerState } from "../map/local_geotiff_preview"
import { natmap_outputs } from "../modelling/components/natmap_soil_component"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid, tileGridStats } from "../modelling/tile_grid"
import {
  DatasetLayer,
  Layer,
  ModelOutputLayer,
  State,
} from "../state"
import { iconForLayerType } from "../utils/util"
import { AegisMapTab } from "../aegis/session"
import { useMapOutputHighlightSnapshot } from "./map_output_highlight"
import { getChartDetailEntry } from "./model_detail"
import { ChartDetailExpandedDialog } from "./model_detail_panel"
import { useExpandedChartId } from "../snapshot/chart_detail_expand"
import {
  GenericLegend,
  GeoserverLayerLegend,
  ModelOutputLayerLegend,
  PaintbrushGeoJSONLegend,
  ShapeLayerLegend,
} from "./sidebar"

function PublicLayerOpacity({
  value,
  onChange,
}: {
  value: number
  onChange: (opacity: number) => void
}) {
  return (
    <label
      className="public-layer-opacity"
      onClick={event => event.stopPropagation()}
      onMouseDown={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
    >
      <span className="public-layer-opacity__label">Opacity</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        aria-label="Layer opacity"
        onChange={event => onChange(Number(event.target.value))}
      />
      <span className="public-layer-opacity__value">{Math.round(value * 100)}</span>
    </label>
  )
}

function PublicCardHeader({
  title,
  icon,
  collapsed,
  onToggle,
  children,
}: {
  title: string
  icon?: string
  collapsed: boolean
  onToggle: () => void
  children?: React.ReactNode
}) {
  return (
    <div className="public-card-header">
      {icon && (
        <span className="public-card-header__icon" aria-hidden>
          <i className={`fas ${icon}`} />
        </span>
      )}
      <span className="public-card-header__title">{title}</span>
      {children}
      <button
        type="button"
        className="public-card-header__btn"
        onClick={onToggle}
        aria-label={collapsed ? `Expand ${title}` : `Collapse ${title}`}
      >
        <i className={`fas fa-chevron-${collapsed ? "down" : "up"}`} />
      </button>
    </div>
  )
}

export interface PublicSidebarProps {
  state: State
  selectLayer: (id: number | undefined) => void
  mutateLayer: (id: number, data: Partial<Layer>) => void
  mapLayerStack: MapStackSlot[]
  reorderMapLayers: (slots: MapStackSlot[]) => void
  localGeotiffLayers: Record<string, LocalGeotiffLayerState>
  mutateLocalGeotiff: (id: string, data: Partial<Pick<LocalGeotiffLayerState, "name" | "opacity" | "visible">>) => void
  removeLocalGeotiff: (id: string) => void
  selectedLocalGeotiffId: string | null
  onSelectLocalGeotiff: (localId: string) => void
  showLayerPalette: () => void
  layerPaletteOpen?: boolean
  getLayerData: (layer: DatasetLayer | ModelOutputLayer) => tileGridStats
  getTileGrid?: (layer: DatasetLayer | ModelOutputLayer) => BooleanTileGrid | NumericTileGrid | CategoricalTileGrid | null
  selectedLayer: Layer | null
  setSelectedLayer: (layer: Layer | null) => void
  model: Data | null
}

export function PublicSidebar({
  state,
  selectLayer,
  mutateLayer,
  mapLayerStack,
  reorderMapLayers,
  localGeotiffLayers,
  mutateLocalGeotiff,
  removeLocalGeotiff,
  selectedLocalGeotiffId,
  onSelectLocalGeotiff,
  showLayerPalette,
  layerPaletteOpen = false,
  getLayerData,
  getTileGrid,
  selectedLayer,
  setSelectedLayer,
  model,
}: PublicSidebarProps) {
  React.useEffect(() => {
    setSelectedLayer(state.selectedLayer === undefined ? null : state.project.layers[state.selectedLayer])
  }, [setSelectedLayer, state.selectedLayer, state.project.layers])

  const selectedLocalLayer = selectedLocalGeotiffId !== null ? localGeotiffLayers[selectedLocalGeotiffId] : null
  const [layersCardCollapsed, setLayersCardCollapsed] = React.useState(false)
  const [legendCardCollapsed, setLegendCardCollapsed] = React.useState(false)
  const [aegisCardCollapsed, setAegisCardCollapsed] = React.useState(false)
  const highlightedOutputNodeId = useMapOutputHighlightSnapshot().highlightedNodeId

  React.useEffect(() => {
    if (highlightedOutputNodeId == null) return
    document
      .querySelector(`[data-map-output-node="${highlightedOutputNodeId}"]`)
      ?.scrollIntoView({ block: "nearest" })
  }, [highlightedOutputNodeId])

  const [expandedChartId, setExpandedChartId] = useExpandedChartId()
  const expandedChart = React.useMemo(
    () => getChartDetailEntry(model, expandedChartId),
    [model, expandedChartId]
  )

  const showLegendCard = (() => {
    if (selectedLocalLayer != null) return false
    if (selectedLayer == null) return false
    if (selectedLayer.type === "ModelOutputLayer" || selectedLayer.type === "DatasetLayer") {
      return getLayerData(selectedLayer).type != null
    }
    return (
      selectedLayer.type === "IMDLayer" ||
      selectedLayer.type === "KewPointLayer" ||
      selectedLayer.type === "WFSLayer" ||
      selectedLayer.type === "ShapeLayer" ||
      (selectedLayer.type === "GeoserverLayer" && !!selectedLayer.fill) ||
      (selectedLayer.type === "KewShapeLayer" && selectedLayer.metric.name !== "none") ||
      selectedLayer.type === "PaintbrushGeoJSONLayer"
    )
  })()

  return (
    <div className="public-map-chrome">
      <div className={`public-layers-slot${layersCardCollapsed ? " is-collapsed" : ""}`}>
        <PublicCardHeader
          title="Layers"
          icon="fa-layer-group"
          collapsed={layersCardCollapsed}
          onToggle={() => setLayersCardCollapsed(collapsed => !collapsed)}
        >
          <button
            type="button"
            className={`public-card-header__btn${layerPaletteOpen ? " is-active" : ""}`}
            onClick={showLayerPalette}
            aria-label="Add layer"
            aria-expanded={layerPaletteOpen}
            title="Add layer"
          >
            <i className="fas fa-plus" />
          </button>
        </PublicCardHeader>
        <div className="public-card-body d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
          <div
            className="flex-grow-1 bg-white"
            style={{ overflowY: "auto", maxHeight: "min(360px, calc(100vh - 380px))" }}
            onClick={() => selectLayer(undefined)}
          >
            <ReactSortable
              list={mapLayerStack.slice().reverse().map(slot => ({ id: slotToKey(slot) }))}
              setList={(list: { id: string }[]) => {
                const nextSlots = list.map(item => keyToSlot(item.id)).reverse()
                reorderMapLayers(nextSlots)
              }}
              filter=".public-layer-opacity, .public-layer-info"
              preventOnFilter={false}
            >
              {mapLayerStack.slice().reverse().map(slot => {
                if (slot.kind === "project") {
                  const id = slot.layerId
                  const layer = state.project.layers[id]
                  if (layer === undefined) return null
                  const isDeleted = layer.type == "DatasetLayer" && layer.deleted === true
                  const paletteColor = (layer.type === "ModelOutputLayer" || layer.type === "DatasetLayer") && layer.colors?.[0] && layer.colors[0][3] !== 0
                    ? `rgba(${layer.colors[0].join(",")})`
                    : null
                  const iconColor = layer.type === "ShapeLayer"
                    ? `rgb(${layer.colors.fill.join(",")})`
                    : paletteColor ?? "rgb(96,96,96)"
                  return (
                    <div
                      key={slotToKey(slot)}
                      data-map-output-node={layer.type === "ModelOutputLayer" ? layer.nodeId : undefined}
                    >
                      <LayerListRow
                        name={layer.name}
                        iconClassName={iconForLayerType(layer.type)}
                        iconColor={iconColor}
                        appearance="checkbox"
                        visible={Boolean(layer.visible)}
                        selected={id === state.selectedLayer && selectedLocalGeotiffId === null}
                        raised={id === state.selectedLayer && selectedLocalGeotiffId === null}
                        highlighted={layer.type === "ModelOutputLayer" && layer.nodeId === highlightedOutputNodeId}
                        dimmed={isDeleted}
                        title={isDeleted ? "Dataset is unavailable." : ""}
                        info={layer.comment?.trim() || undefined}
                        onSelect={(e) => {
                          e.stopPropagation()
                          selectLayer(id === state.selectedLayer ? undefined : id)
                        }}
                        onToggleVisibility={(e) => {
                          e.stopPropagation()
                          mutateLayer(id, { visible: !layer.visible })
                        }}
                      />
                      {id === state.selectedLayer && selectedLocalGeotiffId === null && (
                        <PublicLayerOpacity
                          value={layer.opacity ?? 1}
                          onChange={opacity => mutateLayer(id, { opacity })}
                        />
                      )}
                    </div>
                  )
                }

                const local = localGeotiffLayers[slot.localId]
                const name = local?.name ?? "Local GeoTIFF"
                return (
                  <div key={slotToKey(slot)}>
                    <LayerListRow
                      name={name}
                      iconClassName="fa-image"
                      iconColor="rgb(96,96,96)"
                      appearance="checkbox"
                      visible={local?.visible !== false}
                      selected={slot.localId === selectedLocalGeotiffId}
                      raised={slot.localId === selectedLocalGeotiffId}
                      title="Local GeoTIFF — not saved with the project"
                      onSelect={(e) => {
                        e.stopPropagation()
                        onSelectLocalGeotiff(slot.localId)
                      }}
                      onToggleVisibility={(e) => {
                        e.stopPropagation()
                        if (!local) return
                        mutateLocalGeotiff(slot.localId, { visible: !local.visible })
                      }}
                      deleteTitle="Remove local preview"
                      onDelete={(e) => {
                        e.stopPropagation()
                        removeLocalGeotiff(slot.localId)
                      }}
                    />
                    {slot.localId === selectedLocalGeotiffId && local && (
                      <PublicLayerOpacity
                        value={local.opacity}
                        onChange={opacity => mutateLocalGeotiff(slot.localId, { opacity })}
                      />
                    )}
                  </div>
                )
              })}
            </ReactSortable>
          </div>
          <ChartDetailExpandedDialog
            entry={expandedChart}
            contained
            onClose={() => setExpandedChartId(null)}
          />
        </div>
      </div>
      <div className={`public-legend-slot${!showLegendCard ? " d-none" : ""}${legendCardCollapsed ? " is-collapsed" : ""}`}>
        <PublicCardHeader
          title="Legend"
          icon="fa-book-open"
          collapsed={legendCardCollapsed}
          onToggle={() => setLegendCardCollapsed(collapsed => !collapsed)}
        />
        <div className="public-legend-body">
          {(selectedLayer?.type == "ModelOutputLayer" || selectedLayer?.type == "DatasetLayer") && (
            <ModelOutputLayerLegend
              layer={selectedLayer}
              getLayerData={getLayerData}
              mutateColors={styleUpdate => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, styleUpdate)}
              updateBounds={(overrideBounds, bounds) => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, { overrideBounds, bounds })}
              toggle={false}
              model={model}
              tileGrid={getTileGrid?.(selectedLayer) ?? null}
              metaVariant="public"
            />
          )}
          {selectedLayer?.type == "ShapeLayer" && (
            <ShapeLayerLegend
              layer={selectedLayer}
              mutate={data => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, data)}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "GeoserverLayer" && selectedLayer.fill && (
            <GeoserverLayerLegend
              layer={selectedLayer}
              mutate={data => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, data)}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "IMDLayer" && (
            <GenericLegend
              min={`Least (${selectedLayer.property.min}${selectedLayer.property.min == 10 ? "th Decile" : ""})`}
              max={`Most (${selectedLayer.property.max}${selectedLayer.property.min == 10 ? "st Decile" : ""})`}
              fill={selectedLayer.fill}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "KewPointLayer" && (
            <GenericLegend
              min={`${selectedLayer.min ?? "Least"}`}
              max={`${selectedLayer.max ?? "Most"}`}
              fill={selectedLayer.fill}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "WFSLayer" && (
            <GenericLegend
              min={natmap_outputs[selectedLayer.propIdx].min}
              max={natmap_outputs[selectedLayer.propIdx].max}
              fill={selectedLayer.fill}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "KewShapeLayer" && selectedLayer.metric.name !== "none" && (
            <GenericLegend
              min={`${selectedLayer.min?.toFixed(4) ?? "Least"}`}
              max={`${selectedLayer.max?.toFixed(4) ?? "Most"}`}
              fill={selectedLayer.fill}
              toggle={false}
            />
          )}
          {selectedLayer?.type == "PaintbrushGeoJSONLayer" && (
            <PaintbrushGeoJSONLegend
              layer={selectedLayer}
              toggle={false}
              mutate={data => state.selectedLayer !== undefined && mutateLayer(state.selectedLayer, data)}
            />
          )}
        </div>
      </div>
      <div className={`public-aegis-slot${aegisCardCollapsed ? " is-collapsed" : ""}`}>
        <AegisMapTab
          model={model}
          floating
          collapsed={aegisCardCollapsed}
          onCollapse={() => setAegisCardCollapsed(collapsed => !collapsed)}
        />
      </div>
    </div>
  )
}
